// app/javascript/controllers/outlet_sync_controller.js
// Robust & ringkas: deteksi turbo stream + fallback MutationObserver + timeout
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["spinner", "label"]
  static values = { url: String, timeoutMs: { type: Number, default: 120000 } }

  connect() {
    this._boundHandler = this._handleTurboStream.bind(this)
    this._timeoutId = null
    this._observer = null
  }

  disconnect() {
    this._cleanupAll()
  }

  async sync(event) {
    event.preventDefault()
    const btn = event.currentTarget
    if (!btn) return

    // tampilkan spinner & disable
    this._setBusy(btn, true)

    // pasang listener turbo stream (satu listener global)
    document.addEventListener("turbo:before-stream-render", this._boundHandler)

    // pasang observer untuk fallback: deteksi perubahan DOM pada target kita
    this._startObserver()

    // fallback timeout supaya spinner tidak selamanya
    this._timeoutId = setTimeout(() => {
      console.warn("Sinkron timeout: hentikan spinner")
      this._complete(btn)
    }, this.timeoutMsValue)

    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content || ""
      await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html, application/json, text/html, */*",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        body: JSON.stringify({ async: true })
      })
      // tidak menghentikan spinner di sini — tunggu broadcast/DOM change
    } catch (err) {
      console.error("Network error saat sinkron:", err)
      this._complete(btn)
    }
  }

  // handler turbo stream: cek beberapa lokasi untuk target yang relevant
  _handleTurboStream(event) {
    try {
      const streamEl = event?.detail?.streamElement || event?.target
      if (!streamEl) return

      // 1) cek attribute target langsung (biasanya ada pada <turbo-stream>)
      const directTarget = streamEl.getAttribute?.("target")
      if (directTarget && this._isOurTarget(directTarget)) {
        this._completeButtonAndCleanup()
        return
      }

      // 2) kadang turbo mengemas <template> yang mengandung <turbo-stream>; cari child
      const turboChild = streamEl.querySelector?.("turbo-stream")
      const childTarget = turboChild?.getAttribute?.("target")
      if (childTarget && this._isOurTarget(childTarget)) {
        this._completeButtonAndCleanup()
        return
      }

      // 3) fallback: periksa HTML payload apakah mengandung id target kami
      const html = (streamEl.innerHTML || streamEl.outerHTML || "").toString()
      if (html.includes('id="toast-payload"') || html.includes('id="outlets_table_body"')) {
        this._completeButtonAndCleanup()
        return
      }
    } catch (e) {
      console.error("Error di turbo handler:", e)
      this._completeButtonAndCleanup()
    }
  }

  // kecil: cek apakah nama target yang diterima adalah target yang kita tunggu
  _isOurTarget(name) {
    return name === "toast-payload" || name === "outlets_table_body"
  }

  // bila stream/DOM terdeteksi, hentikan spinner dan cleanup
  _completeButtonAndCleanup() {
    const btn = document.getElementById("sync-outlets-btn") || this.element.querySelector("button")
    this._complete(btn)
  }

  // hentikan spinner, bersihkan listener/observer/timeout
  _complete(button) {
    if (button) this._setBusy(button, false)
    this._cleanupAll()
  }

  _cleanupAll() {
    document.removeEventListener("turbo:before-stream-render", this._boundHandler)
    if (this._timeoutId) { clearTimeout(this._timeoutId); this._timeoutId = null }
    if (this._observer) { try { this._observer.disconnect() } catch(_){}; this._observer = null }
  }

  // MutationObserver: pantau body untuk perubahan yang menyertakan target ID
  _startObserver() {
    if (this._observer) return
    this._observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // cek node-node baru apakah mengandung target id
        for (const n of [...(m.addedNodes || [])]) {
          if (n && n.nodeType === Node.ELEMENT_NODE) {
            const el = /** @type {Element} */ (n)
            if (el.id === "toast-payload" || el.id === "outlets_table_body") {
              this._completeButtonAndCleanup()
              return
            }
            if ((el.innerHTML || "").includes('id="toast-payload"') || (el.innerHTML || "").includes('id="outlets_table_body"')) {
              this._completeButtonAndCleanup()
              return
            }
          }
        }
      }
    })
    this._observer.observe(document.body, { childList: true, subtree: true })
  }

  // toggle spinner & label
  _setBusy(button, busy = true) {
    button.disabled = busy
    button.setAttribute("aria-disabled", busy ? "true" : "false")
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !busy)
    if (this.hasLabelTarget) this.labelTarget.classList.toggle("opacity-60", busy)
    if (typeof window.lucide !== "undefined" && window.lucide.replace) {
      try { window.lucide.replace() } catch (_) {}
    }
  }
}
